import { useLocalStorage } from "@mantine/hooks";
import OpenApiClient from "./OpenApiClient";

export const AuthTokenLocalStorageKey = "AuthToken";

// Ссылка на безопасное хранилище Яндекса. Изначально null.
let yandexSafeStorage: Storage | null = null;

let currentAuthToken: string | null = null;

/**
 * Регистрирует SafeStorage от Яндекс.Игр.
 * Вызывается из адаптера после инициализации YSDK.
 */
export function registerYandexStorage(storage: Storage): void {
	yandexSafeStorage = storage;
	log.info("Yandex SafeStorage registered in Auth module");

	// Попытка восстановить сессию, если localStorage был очищен (проблема iOS)
	try {
		const yaToken = storage.getItem(AuthTokenLocalStorageKey);
		const localToken = localStorage.getItem(AuthTokenLocalStorageKey);

		if (yaToken && !localToken) {
			log.info("Restoring auth token from Yandex SafeStorage to LocalStorage");
			setAuthToken(yaToken);
		} else if (currentAuthToken && !yaToken) {
			// Если у нас есть токен в памяти/local, но нет в яндексе - синхронизируем в яндекс
			try {
				log.info("Restoring auth token from LocalStorage to Yandex SafeStorage");
				storage.setItem(AuthTokenLocalStorageKey, currentAuthToken);
			} catch (e) {
				log.error(e);
			}
		}
	} catch (err) {
		log.error(err);
	}
}

export function setAuthToken(token: string): void {
	currentAuthToken = token;

	// 1. Пишем в LocalStorage (стандартное поведение)
	try {
		localStorage.setItem(AuthTokenLocalStorageKey, token);
	} catch (err) {
		log.error(err);
	}

	// 2. Дублируем в Яндекс (если доступен)
	if (yandexSafeStorage) {
		try {
			log.info("Updating auth token in Yandex SafeStorage");
			yandexSafeStorage.setItem(AuthTokenLocalStorageKey, token);
		} catch (err) {
			log.error("Failed to write to Yandex storage", err);
		}
	}
}

export function getAuthToken(): string | null {
	let token: string | null = null;

	// 1. Пробуем читать из LocalStorage
	try {
		token = localStorage.getItem(AuthTokenLocalStorageKey);
	} catch (err) {
		log.error(err);
	}

	// 2. Если не нашли и есть Яндекс хранилище - пробуем оттуда
	if (!token && yandexSafeStorage) {
		try {
			log.info("No token in localStorage, trying Yandex SafeStorage");
			token = yandexSafeStorage.getItem(AuthTokenLocalStorageKey);
			// Если нашли в Яндексе, но не было в LocalStorage - имеет смысл восстановить в currentAuthToken
			// Но так как эта функция должна быть чистой (getter), мы просто вернем значение.
			// Синхронизация произойдет при повторном вызове setAuthToken или при инициализации.
		} catch (err) {
			log.error("Failed to read from Yandex storage", err);
		}
	}

	if (token == null) {
		log.info("No token in localStorage or Yandex SafeStorage");
	}

	// 3. Фолбек на переменную в памяти
	return token || currentAuthToken;
}

export function authTokenExists(): boolean {
	try {
		// Проверка в LocalStorage
		if (localStorage.getItem(AuthTokenLocalStorageKey) != null) return true;

		// Проверка в Yandex Storage
		if (yandexSafeStorage && yandexSafeStorage.getItem(AuthTokenLocalStorageKey) != null) return true;
	} catch (err) {
		log.error(err);
	}
	return currentAuthToken != null;
}

/** Хук для получения auth token. Подписывается на изменения в local storage */
export function useAuthToken() {
	try {
		// useLocalStorage подписывается на события window, поэтому когда мы делаем setItem в setAuthToken,
		// этот хук обновится, даже если восстановление произошло из Яндекса.
		const [token] = useLocalStorage({ key: AuthTokenLocalStorageKey });
		return token;
	} catch (error) {
		log.error(error);
		return null;
	}
}

export async function logout(): Promise<void> {
	// 1. Удаляем из LocalStorage
	try {
		localStorage.removeItem(AuthTokenLocalStorageKey);
	} catch (err) {
		log.error(err);
	}

	// 2. Удаляем из Yandex Storage
	if (yandexSafeStorage) {
		try {
			yandexSafeStorage.removeItem(AuthTokenLocalStorageKey);
		} catch (err) {
			log.error("Failed to remove from Yandex storage", err);
		}
	}

	currentAuthToken = null;

	// await signOutFromFirebase();
	await OpenApiClient.Auth.fxIdWebFeaturesAuthLogoutEndpoint();
}
